import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, BadgeCheck, GraduationCap, Trophy } from "lucide-react";
import Seo, { breadcrumbSchema } from "../components/Seo";
import { Reveal, Eyebrow, BtnPrimary, BtnGhost, CtaBand, Crumbs } from "../components/Shared";
import { COACHES, SITE } from "../data/mock";

const coachSchema = (c) => ({
  "@context": "https://schema.org",
  "@type": "ProfilePage",
  "@id": `${SITE.url}/coaches/${c.slug}#profile`,
  url: `${SITE.url}/coaches/${c.slug}`,
  mainEntity: {
    "@type": "Person",
    name: c.name,
    jobTitle: c.role,
    description: c.bio,
    ...(c.photo ? { image: `${SITE.url}${c.photo}` } : {}),
    ...(c.email ? { email: c.email } : {}),
    worksFor: { "@id": `${SITE.url}/#business` },
    knowsAbout: c.certs,
  },
});

export default function Coach() {
  const { slug } = useParams();
  const coach = COACHES.find((c) => c.slug === slug);
  if (!coach) return <Navigate to="/about" replace />;

  const others = COACHES.filter((c) => c.slug !== slug);
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: `Coach ${coach.name}`, path: `/coaches/${coach.slug}` },
  ];

  return (
    <>
      <Seo
        title={`Coach ${coach.name} — ${coach.role.replace("Coach · ", "")} | Boston Iron Grit`}
        description={coach.bio.slice(0, 158)}
        path={`/coaches/${coach.slug}`}
        ogType="profile"
        schemas={[breadcrumbSchema(crumbs), coachSchema(coach)]}
      />

      {/* ---------------- HERO ---------------- */}
      <section className="bg-[#0A0A0B] pt-14 pb-16 lg:pb-24" data-testid="coach-hero">
        <div className="max-w-7xl mx-auto px-6">
          <Crumbs items={crumbs} />
          <div className="mt-8 grid lg:grid-cols-[1fr_420px] gap-14 items-start">
            <Reveal>
              <Eyebrow>{coach.role}</Eyebrow>
              <h1 className="font-heading font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight">
                Coach {coach.name.split(" ")[0]}<br />
                <span className="text-neutral-600">{coach.name.split(" ").slice(1).join(" ")}.</span>
              </h1>
              <p className="mt-4 font-mono text-[12px] uppercase tracking-[0.2em] text-[#E56A79]">{coach.creds}</p>
              <div className="mt-8 space-y-5 max-w-2xl">
                {coach.longBio.map((p) => (
                  <p key={p.slice(0, 48)} className="text-neutral-300 text-base leading-relaxed">{p}</p>
                ))}
              </div>
              {coach.email && (
                <p className="mt-6 font-mono text-[13px] text-neutral-400">
                  Email: <a href={`mailto:${coach.email}`} className="text-[#E56A79] hover:text-white transition-colors" data-testid="coach-email-link">{coach.email}</a>
                </p>
              )}
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <BtnPrimary to="/get-started">Book a Free Goal Review</BtnPrimary>
                <BtnGhost to="/schedule">See the Schedule</BtnGhost>
              </div>
            </Reveal>
            <Reveal delay={150}>
              {coach.photo ? (
                <div className="relative border border-white/10 overflow-hidden">
                  <img src={coach.photo} alt={`Coach ${coach.name} at Boston Iron Grit in West Roxbury`} className="w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/60 via-transparent to-transparent" aria-hidden="true" />
                </div>
              ) : (
                <div className="aspect-[3/2] bg-[#121214] border border-white/10 flex items-center justify-center">
                  <span className="font-heading font-extrabold text-7xl text-[#E56A79]/40">{coach.name.charAt(0)}</span>
                </div>
              )}
              {coach.photo2 && (
                <div className="relative border border-white/10 overflow-hidden mt-5" data-testid="coach-photo-2">
                  <img src={coach.photo2} alt={coach.photo2Alt || `Coach ${coach.name} at Boston Iron Grit`} className="w-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="mt-6 p-6 bg-[#111113] border border-white/5">
                <h2 className="font-heading font-bold uppercase text-white text-sm tracking-wide flex items-center gap-2">
                  <BadgeCheck size={16} className="text-[#9D0423]" /> Certifications
                </h2>
                <ul className="mt-4 grid grid-cols-1 gap-2" data-testid="coach-certs">
                  {coach.certs.map((cert) => (
                    <li key={cert} className="font-mono text-[12px] uppercase tracking-[0.1em] text-neutral-400">— {cert}</li>
                  ))}
                </ul>
                {coach.education && (
                  <>
                    <h2 className="mt-6 font-heading font-bold uppercase text-white text-sm tracking-wide flex items-center gap-2">
                      <GraduationCap size={16} className="text-[#9D0423]" /> Education
                    </h2>
                    <ul className="mt-4 grid grid-cols-1 gap-2">
                      {coach.education.map((e) => (
                        <li key={e} className="font-mono text-[12px] tracking-[0.04em] text-neutral-400">— {e}</li>
                      ))}
                    </ul>
                  </>
                )}
                {coach.achievements && (
                  <>
                    <h2 className="mt-6 font-heading font-bold uppercase text-white text-sm tracking-wide flex items-center gap-2">
                      <Trophy size={16} className="text-[#9D0423]" /> Athletic Achievements
                    </h2>
                    <ul className="mt-4 grid grid-cols-1 gap-2">
                      {coach.achievements.map((a) => (
                        <li key={a} className="font-mono text-[12px] tracking-[0.04em] text-neutral-400">— {a}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------- REST OF TEAM ---------------- */}
      <section className="bg-[#0D0D0F] py-16 border-y border-white/5" data-testid="coach-team-strip">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>The Rest of the Team</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white leading-[0.95] tracking-tight">
              Meet the other coaches.
            </h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {others.map((c, i) => (
              <Reveal key={c.slug} delay={i * 70}>
                <Link
                  to={`/coaches/${c.slug}`}
                  className="block group bg-[#121214] border border-white/5 hover:border-[#9D0423]/50 transition-colors h-full"
                  data-testid={`coach-mini-${c.slug}`}
                >
                  {c.photo ? (
                    <div className="aspect-[3/2] overflow-hidden">
                      <img src={c.photo} alt={`Coach ${c.name}`} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" loading="lazy" />
                    </div>
                  ) : (
                    <div className="aspect-[3/2] bg-[#0F0F11] flex items-center justify-center">
                      <span className="font-heading font-extrabold text-4xl text-[#E56A79]/40">{c.name.charAt(0)}</span>
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="font-heading font-bold uppercase text-white text-base tracking-tight">Coach {c.name}</h3>
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500 mt-1">{c.role}</p>
                    <span className="mt-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-neutral-500 group-hover:text-[#9D0423] transition-colors">
                      Full Bio <ArrowRight size={12} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        title={`Train with Coach ${coach.name.split(" ")[0]}.`}
        sub="Book a free 20-minute goal review, meet the coaching team, and get an honest plan for your goals — no pressure, no obligation."
      />
    </>
  );
}
